import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../styles/ThemeProvider';
import { useCommonStyles } from '../../styles/useCommonStyles';

interface QuickStartBannerProps {
  itemCount: number;
  onAddItem: () => void;
  onDismiss?: () => void;
}

export function QuickStartBanner({ itemCount, onAddItem, onDismiss }: QuickStartBannerProps) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.lg,
          margin: theme.spacing.lg,
          position: 'relative',
        },
        dismissButton: {
          position: 'absolute',
          top: theme.spacing.md,
          right: theme.spacing.md,
          zIndex: 1,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: theme.spacing.md,
          gap: theme.spacing.md,
        },
        iconContainer: {
          width: 40,
          height: 40,
          borderRadius: 20,
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          alignItems: 'center',
          justifyContent: 'center',
        },
        title: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          flex: 1,
          paddingRight: theme.spacing.xl,
        },
        stepsContainer: {
          marginBottom: theme.spacing.lg,
          gap: theme.spacing.sm,
        },
        stepText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
      }),
    [theme],
  );

  const [dismissed, setDismissed] = useState(false);

  if (itemCount > 2 || dismissed) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  const getContent = () => {
    if (itemCount === 0) {
      return {
        icon: 'rocket-outline' as const,
        title: 'Get started in 3 easy steps',
        steps: [
          '📸 Snap a photo of an item',
          '🤖 AI identifies and values it',
          '📊 Track your cache value',
        ],
        cta: 'Add Your First Item',
      };
    } else if (itemCount === 1) {
      return {
        icon: 'trending-up-outline' as const,
        title: 'Great start! Keep going',
        steps: [
          '✓ First item added',
          '📱 Add 4 more to unlock insights',
          '💰 See your total portfolio value',
        ],
        cta: 'Add Another Item',
      };
    } else {
      return {
        icon: 'checkmark-circle-outline' as const,
        title: 'Almost there!',
        steps: [
          `✓ ${itemCount} items added`,
          '📊 Portfolio tracking active',
          '🎯 Add 3 more for full analytics',
        ],
        cta: 'Continue Adding',
      };
    }
  };

  const content = getContent();

  return (
    <View style={styles.container}>
      <TouchableOpacity
        style={styles.dismissButton}
        onPress={handleDismiss}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <Ionicons name="close" size={20} color={theme.colors.textSecondary} />
      </TouchableOpacity>

      <View style={styles.header}>
        <View style={styles.iconContainer}>
          <Ionicons name={content.icon} size={24} color={theme.colors.accent} />
        </View>
        <Text style={styles.title}>{content.title}</Text>
      </View>

      <View style={styles.stepsContainer}>
        {content.steps.map((step, index) => (
          <Text key={index} style={styles.stepText}>
            {step}
          </Text>
        ))}
      </View>

      <TouchableOpacity style={commonStyles.primaryButton} onPress={onAddItem}>
        <Ionicons name="sparkles-outline" size={20} color={theme.colors.onAccent} />
        <Text style={commonStyles.primaryButtonText}>{content.cta}</Text>
        <Ionicons name="arrow-forward" size={16} color={theme.colors.onAccent} />
      </TouchableOpacity>
    </View>
  );
}
